import React from 'react'
import { cn } from '../../utils/cn'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  touchFriendly?: boolean
  loading?: boolean
  children: React.ReactNode
}

const variantClasses = {
  primary: 'bg-primary-600 hover:bg-primary-700 text-white shadow-sm hover:shadow-md',
  secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-900 shadow-sm hover:shadow-md',
  outline: 'border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 shadow-sm hover:shadow-md',
  ghost: 'hover:bg-gray-100 text-gray-700',
}

const sizeClasses = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
  xl: 'px-8 py-4 text-lg',
}

const touchSizeClasses = {
  sm: 'min-h-[44px] min-w-[44px] px-4 py-2 text-sm',
  md: 'min-h-[44px] min-w-[44px] px-5 py-3 text-base',
  lg: 'min-h-[48px] min-w-[48px] px-6 py-3 text-base',
  xl: 'min-h-[48px] min-w-[48px] px-8 py-4 text-lg',
}

export function Button({
  variant = 'primary',
  size = 'md',
  touchFriendly = true,
  loading = false,
  className,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const sizeClass = touchFriendly ? touchSizeClasses[size] : sizeClasses[size]

  return (
    <button
      className={cn(
        // Base styles
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
        'transition-all duration-200 ease-in-out',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        // Touch-friendly styles
        touchFriendly && 'active:scale-95 touch-manipulation',
        // Variant and size
        variantClasses[variant],
        sizeClass,
        // Loading state
        loading && 'cursor-wait',
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  )
}

// Touch-friendly icon button
interface IconButtonProps extends Omit<ButtonProps, 'children'> {
  icon: React.ReactNode
  'aria-label': string
}

export function IconButton({
  icon,
  size = 'md',
  touchFriendly = true,
  className,
  ...props
}: IconButtonProps) {
  const iconSizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-7 w-7',
  }

  return (
    <Button
      size={size}
      touchFriendly={touchFriendly}
      className={cn(
        'aspect-square p-0',
        touchFriendly ? 'min-h-[44px] min-w-[44px]' : '',
        className
      )}
      {...props}
    >
      <span className={iconSizeClasses[size]}>{icon}</span>
    </Button>
  )
}